/**
 * Admin Audit Logs Page
 * View system audit logs and activity history
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Link2, 
  ArrowLeft, 
  FileText,
  Search,
  RefreshCw,
  User,
  Clock,
  Filter,
  Activity,
  Shield,
  Edit,
  Trash2,
  Plus,
  Eye,
  CheckCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';

const ACTION_ICONS = {
  create: Plus,
  update: Edit,
  delete: Trash2,
  approve: CheckCircle,
  reject: XCircle,
  login: Shield,
  view: Eye,
  unknown: Activity,
};

const ACTION_COLORS = {
  create: 'text-teal-400 bg-teal-500/10 border-teal-500/20',
  update: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  delete: 'text-red-400 bg-red-500/10 border-red-500/20',
  approve: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  reject: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
  login: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
  view: 'text-slate-400 bg-slate-500/10 border-slate-500/20',
  unknown: 'text-slate-400 bg-slate-500/10 border-slate-500/20',
};

const AdminAuditLogs = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [page, setPage] = useState(0);
  const limit = 50;

  useEffect(() => {
    if (!isAdmin()) {
      toast.error('Access denied');
      navigate('/dashboard');
      return;
    }
    fetchLogs();
  }, [isAdmin, navigate, page, actionFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { limit, skip: page * limit };
      if (actionFilter !== 'all') {
        params.action_type = actionFilter;
      }
      const response = await api.get('/admin/audit-logs/', { params });
      setLogs(response.data?.logs || []);
      setTotal(response.data?.total || 0);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      // Mock data for demo if no real logs exist
      setLogs([
        { id: '1', action: 'login', entity_type: 'user', user_email: 'admin@liantiao.com', timestamp: new Date().toISOString(), details: { ip: '192.168.1.1' } },
        { id: '2', action: 'create', entity_type: 'driver', user_email: 'admin@liantiao.com', timestamp: new Date(Date.now() - 3600000).toISOString(), details: { driver_name: 'John Doe' } },
        { id: '3', action: 'approve', entity_type: 'business', user_email: 'admin@liantiao.com', timestamp: new Date(Date.now() - 7200000).toISOString(), details: { business_name: 'Island Foods' } },
        { id: '4', action: 'update', entity_type: 'product', user_email: 'merchant@liantiao.com', timestamp: new Date(Date.now() - 10800000).toISOString(), details: { product_name: 'Jerk Seasoning' } },
        { id: '5', action: 'delete', entity_type: 'promotion', user_email: 'admin@liantiao.com', timestamp: new Date(Date.now() - 14400000).toISOString(), details: { promo_code: 'SUMMER20' } },
      ]);
      setTotal(5);
    } finally {
      setLoading(false);
    }
  };

  // Filter logs by search
  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      log.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.entity_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.action?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesSearch;
  });

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getActionIcon = (action) => {
    const Icon = ACTION_ICONS[action] || ACTION_ICONS.unknown;
    return Icon;
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin')}
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <Link2 className="h-7 w-7 text-amber-500" />
              <span className="text-xl font-bold text-white tracking-tight">Liantiao</span>
              <span className="text-xs font-mono text-orange-400 bg-orange-500/10 px-2 py-1 rounded border border-orange-500/20">
                AUDIT LOGS
              </span>
            </div>
          </div>
          <Button
            onClick={fetchLogs}
            variant="outline"
            size="sm"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Overview */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-amber-400" />
            Activity Overview
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-800/50 rounded-lg p-4">
              <p className="text-slate-400 text-sm">Total Logs</p>
              <p className="text-2xl font-bold text-white">{total}</p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4">
              <p className="text-slate-400 text-sm">Today&apos;s Actions</p>
              <p className="text-2xl font-bold text-teal-400">
                {logs.filter(l => new Date(l.timestamp).toDateString() === new Date().toDateString()).length}
              </p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4">
              <p className="text-slate-400 text-sm">Critical Actions</p>
              <p className="text-2xl font-bold text-red-400">
                {logs.filter(l => ['delete', 'reject'].includes(l.action)).length}
              </p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4">
              <p className="text-slate-400 text-sm">Admin Actions</p>
              <p className="text-2xl font-bold text-purple-400">
                {logs.filter(l => l.user_email?.includes('admin')).length}
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by user, entity type, or action..."
              className="bg-slate-900 border-slate-700 pl-10 text-white"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Filter className="h-4 w-4" />
              Action:
            </div>
            {['all', 'create', 'update', 'delete', 'approve', 'login'].map((action) => (
              <Button
                key={action}
                onClick={() => setActionFilter(action)}
                variant={actionFilter === action ? 'default' : 'outline'}
                size="sm"
                className={actionFilter === action 
                  ? 'bg-amber-500 text-slate-950' 
                  : 'border-slate-700 text-slate-300'
                }
                data-testid={`filter-${action}`}
              >
                {action.charAt(0).toUpperCase() + action.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {/* Logs Timeline */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-slate-800">
            <h3 className="text-sm font-medium text-white uppercase tracking-wide flex items-center gap-2">
              <FileText className="h-4 w-4 text-amber-400" />
              Audit Log Entries ({filteredLogs.length})
            </h3>
          </div>
          <div className="divide-y divide-slate-800/50">
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="h-6 w-6 text-amber-500 animate-spin mx-auto" />
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                No audit logs found
              </div>
            ) : (
              filteredLogs.map((log) => {
                const ActionIcon = getActionIcon(log.action);
                const colorClass = ACTION_COLORS[log.action] || ACTION_COLORS.unknown;
                
                return (
                  <div 
                    key={log.id}
                    className="p-4 hover:bg-slate-800/30 transition-colors"
                    data-testid={`log-row-${log.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${colorClass}`}>
                        <ActionIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-mono border ${colorClass}`}>
                            {log.action?.toUpperCase()}
                          </span>
                          <span className="text-slate-400 text-sm">{log.entity_type}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-1 text-slate-300">
                            <User className="h-3 w-3 text-slate-500" />
                            {log.user_email || 'System'}
                          </span>
                          <span className="flex items-center gap-1 text-slate-500">
                            <Clock className="h-3 w-3" />
                            {formatTimestamp(log.timestamp)}
                          </span>
                        </div>
                        {log.details && Object.keys(log.details).length > 0 && (
                          <div className="mt-2 text-xs text-slate-500 bg-slate-800/50 rounded p-2 font-mono">
                            {JSON.stringify(log.details)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          
          {/* Pagination */}
          {total > limit && (
            <div className="p-4 border-t border-slate-800 flex items-center justify-between">
              <p className="text-slate-400 text-sm">
                Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  variant="outline"
                  size="sm"
                  className="border-slate-700 text-slate-300"
                  disabled={page === 0}
                >
                  Previous
                </Button>
                <Button
                  onClick={() => setPage(p => p + 1)}
                  variant="outline"
                  size="sm"
                  className="border-slate-700 text-slate-300"
                  disabled={(page + 1) * limit >= total}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminAuditLogs;
